import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Loader2, CheckCircle2, AlertTriangle, CreditCard, KeyRound, ExternalLink, MinusCircle, GitCommit } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ok: { label: "Operational", tone: "emerald", icon: CheckCircle2 },
  configured: { label: "Configured", tone: "emerald", icon: CheckCircle2 },
  quota_exhausted: { label: "Out of credits", tone: "rose", icon: CreditCard },
  rate_limited: { label: "Rate limited", tone: "amber", icon: AlertTriangle },
  auth_failed: { label: "Key rejected", tone: "rose", icon: KeyRound },
  error: { label: "Error", tone: "rose", icon: AlertTriangle },
  not_configured: { label: "Not configured", tone: "slate", icon: MinusCircle },
};

const TONE = {
  emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300",
  rose: "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300",
  amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300",
  slate: "bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400",
};

function ProviderCard({ p }) {
  const s = STATUS[p.status] || STATUS.error;
  const Icon = s.icon;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`integration-card-${p.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{p.label}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid={`integration-detail-${p.key}`}>{p.detail}</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 shrink-0 h-7 px-2.5 rounded-full border text-xs font-semibold ${TONE[s.tone]}`} data-testid={`integration-status-${p.key}`}>
          <Icon className="h-3.5 w-3.5" /> {s.label}
        </span>
      </div>
      {p.billing_url && (
        <a
          href={p.billing_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-sky-600 dark:text-sky-400 hover:underline"
          data-testid={`integration-billing-${p.key}`}
        >
          <ExternalLink className="h-3.5 w-3.5" /> Open billing / top up
        </a>
      )}
    </div>
  );
}

export default function DevAdminIntegrations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState(null);
  const [build, setBuild] = useState(null);

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devadmin/integrations/health");
      setProviders(data.providers || []);
      setCheckedAt(data.checked_at ? new Date(data.checked_at * 1000) : new Date());
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not check integrations");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/devadmin/build-info").then(({ data }) => setBuild(data)).catch(() => {});
  }, []);

  const [migStatus, setMigStatus] = useState(null);
  const [migRunning, setMigRunning] = useState(false);
  const [diagClass, setDiagClass] = useState("6");
  const [diagSection, setDiagSection] = useState("A");
  const [diagResult, setDiagResult] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);

  const loadMigStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/devadmin/photo-migration/status");
      setMigStatus(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadMigStatus(); }, [loadMigStatus]);

  const runMigration = async () => {
    setMigRunning(true);
    try {
      const { data } = await api.post("/devadmin/photo-migration/run");
      if (data.already_running) {
        toast.info("Migration is already running — watching progress…");
      } else {
        toast.success("Migration started in the background. Watching progress…");
      }
      // Poll status until inline photos reach 0 (or ~2 min elapsed).
      let done = false;
      for (let i = 0; i < 40 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: st } = await api.get("/devadmin/photo-migration/status");
          setMigStatus(st);
          if (st.migration_complete) {
            done = true;
            toast.success("Photo migration complete — all photos moved out. Reload the roster now.");
          }
        } catch { /* keep polling */ }
      }
      if (!done) toast.info("Still migrating in the background. Keep refreshing status; it will finish shortly.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Migration failed to start");
    } finally { setMigRunning(false); }
  };

  const runDiagnose = async () => {
    setDiagRunning(true);
    setDiagResult(null);
    try {
      const { data } = await api.get(`/devadmin/roster-diagnose?class_name=${encodeURIComponent(diagClass)}&section=${encodeURIComponent(diagSection)}`);
      setDiagResult(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Diagnose failed");
    } finally { setDiagRunning(false); }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="devadmin-integrations-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Activity className="h-7 w-7 text-sky-500" /> Integrations Health
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Live status &amp; quota check for each AI/voice provider. {checkedAt && <span data-testid="integrations-checked-at">Last checked {checkedAt.toLocaleTimeString()}.</span>}
            </p>
          </div>
          <Button onClick={load} disabled={loading} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="integrations-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Refresh
          </Button>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
          <span>OpenAI and Sarvam do not expose a live credit-balance API, so exact remaining credits can't be shown here. This panel confirms each key works and flags <strong>out-of-credits / rate-limited / rejected-key</strong> states so you can top up fast via the billing links.</span>
        </div>

        {build && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="backend-build-badge">
            <span className="inline-flex items-center gap-1.5 font-mono font-semibold text-sky-600 dark:text-sky-400">
              <GitCommit className="h-3.5 w-3.5" /> {build.commit}
            </span>
            {build.commit_subject && <span className="text-slate-500 dark:text-slate-400 truncate max-w-md" title={build.commit_subject}>{build.commit_subject}</span>}
            <span className="text-slate-400 dark:text-slate-500" data-testid="backend-started-at">
              Backend started {new Date(build.started_at).toLocaleString()}
            </span>
          </div>
        )}

        {loading && providers.length === 0 ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="integrations-grid">
            {providers.map((p) => <ProviderCard key={p.key} p={p} />)}
          </div>
        )}

        {/* ---- Roster diagnostics & photo migration (production troubleshooting) ---- */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4" data-testid="roster-diagnostics-card">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Roster diagnostics &amp; photo storage</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Runs the student-roster query stage-by-stage against THIS environment's database and reports timings, collection sizes and indexes. Use it to see exactly why a roster is slow.</p>
          </div>

          {migStatus && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 text-xs" data-testid="photo-migration-status">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className={`font-semibold ${migStatus.migration_complete ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                  {migStatus.migration_complete ? "Photos migrated ✓" : "Inline photos still present ⚠"}
                </span>
                <span className="text-slate-500 dark:text-slate-400">inline remaining: <b>{migStatus.students_inline_remaining + migStatus.teachers_inline_remaining}</b></span>
                <span className="text-slate-500 dark:text-slate-400">photos collection: <b>{migStatus.photos_collection_count}</b></span>
                <span className="text-slate-500 dark:text-slate-400">has_photo=true: <b>{migStatus.students_has_photo_true}</b></span>
              </div>
              <Button onClick={runMigration} disabled={migRunning} size="sm" className="mt-3 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white h-8 text-xs" data-testid="run-photo-migration-btn">
                {migRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null} Run photo migration now
              </Button>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Class</label>
              <input value={diagClass} onChange={(e) => setDiagClass(e.target.value)} className="h-9 w-24 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid="diag-class-input" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">Section</label>
              <input value={diagSection} onChange={(e) => setDiagSection(e.target.value)} className="h-9 w-24 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm" data-testid="diag-section-input" />
            </div>
            <Button onClick={runDiagnose} disabled={diagRunning} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white h-9" data-testid="run-diagnose-btn">
              {diagRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Activity className="h-4 w-4 mr-1.5" />} Diagnose roster
            </Button>
          </div>

          {diagResult && (
            <pre className="rounded-xl bg-slate-900 text-emerald-300 text-[11px] leading-relaxed p-4 overflow-x-auto max-h-96" data-testid="diag-result">
{JSON.stringify(diagResult, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </Layout>
  );
}
